/**
 * blockAuthoringRegistry — Phase 5.3.e sub-machine collapse.
 *
 * Mirror of `dimensionRegistry.ts` for the Phase K.2 / K.3 block-
 * authoring placement steps. Wraps the 47-case switches in
 * `advanceBlockAuthoringWithPoint` + `advanceBlockAuthoringWithText`
 * (`apps/web/src/app/scene/draftStateMachine.blockAuthoring.ts`) as
 * `Partial<Record<DraftStep, AdvanceWithPointFn|AdvanceWithTextFn>>`
 * entries so the unified `ADVANCE_WITH_POINT` / `ADVANCE_WITH_TEXT`
 * registries handle them alongside LINE / CIRCLE / DIM*.
 *
 * The wrappers delegate verbatim — no behaviour change. PR 5.3.e
 * removes the `isBlockAuthoringStep` predicate + delegation block
 * in `advanceDraftSessionWithPoint` / `advanceDraftSessionWithText`.
 *
 * Step list mirrors the predicate exactly; if either drifts, the
 * unified dispatch will miss steps the legacy delegation caught.
 */

import {
  advanceBlockAuthoringWithPoint,
  advanceBlockAuthoringWithText,
} from "../draftStateMachine.blockAuthoring";
import type {
  AdvanceWithPointFn,
  AdvanceWithTextFn,
} from "../draftTypes";

const BLOCK_AUTHORING_STEPS = [
  // Phase K.2a — Linear
  "param-linear-base",
  "param-linear-end",
  "param-linear-label",
  // Phase K.2b — remaining 9 parameter kinds
  "param-point-place",
  "param-basepoint-place",
  "param-visibility-place",
  "param-lookup-place",
  "param-flip-base",
  "param-flip-end",
  "param-flip-label",
  "param-polar-base",
  "param-polar-end",
  "param-polar-label",
  "param-xy-base",
  "param-xy-end",
  "param-xy-label",
  "param-rotation-base",
  "param-rotation-radius-end",
  "param-rotation-label",
  "param-alignment-base",
  "param-alignment-end",
  // Phase K.3 — action placement
  "action-move-pick-parameter",
  "action-move-select-objects",
  "action-move-place",
  "action-scale-pick-parameter",
  "action-scale-select-objects",
  "action-scale-place",
  "action-stretch-pick-parameter",
  "action-stretch-select-objects",
  "action-stretch-frame-corner-1",
  "action-stretch-frame-corner-2",
  "action-stretch-place",
  "action-polar-stretch-pick-parameter",
  "action-polar-stretch-select-objects",
  "action-polar-stretch-frame-corner-1",
  "action-polar-stretch-frame-corner-2",
  "action-polar-stretch-place",
  "action-rotation-pick-parameter",
  "action-rotation-select-objects",
  "action-rotation-place",
  "action-flip-pick-parameter",
  "action-flip-select-objects",
  "action-flip-place",
  "action-array-pick-parameter",
  "action-array-select-objects",
  "action-array-place",
  "action-lookup-pick-parameter",
  "action-lookup-select-objects",
  "action-lookup-place",
] as const;

const blockAuthPointWrapper: AdvanceWithPointFn = (session, point, ctx) =>
  advanceBlockAuthoringWithPoint(session, point, ctx.existingEntities);

const blockAuthTextWrapper: AdvanceWithTextFn = (session, ctx) =>
  advanceBlockAuthoringWithText(session, ctx.text);

export const BLOCK_AUTHORING_POINT_ENTRIES: ReadonlyArray<
  readonly [(typeof BLOCK_AUTHORING_STEPS)[number], AdvanceWithPointFn]
> = BLOCK_AUTHORING_STEPS.map((step) => [step, blockAuthPointWrapper]);

export const BLOCK_AUTHORING_TEXT_ENTRIES: ReadonlyArray<
  readonly [(typeof BLOCK_AUTHORING_STEPS)[number], AdvanceWithTextFn]
> = BLOCK_AUTHORING_STEPS.map((step) => [step, blockAuthTextWrapper]);
